// frontend/src/components/WaiverReturn.js
//
// ESIGN-WAIVER-CONSENT-001 — return-from-SignWell receipt page
// (Zuri spec §16.4 + §16.5).
//
// Trigger: SignWell redirects the customer's browser back to
// `/waivers/return?envelope=<envelopeIdInternal>` after they finish (or
// abandon) signing. Optional URL params:
//   - envelope=<id>        — internal envelope id (compare to localStorage)
//   - status=declined      — SignWell-set decline marker (Zuri §16.4 row 3)
//   - status=expired_token — backfill-flow expired-token surface (Zuri §16.5)
//
// State machine (per §16.4 + §16.5):
//   pending  -> auto-poll GET /api/waivers/me every 2s up to 30s
//                 success     (both timestamps non-null) -> success state
//                 timeout     (30s without confirmation) -> timeout state
//   declined -> show decline state with re-initiate CTA (fresh envelope)
//   expired  -> show expired-token state with login CTA
//   error    -> show error state with retry
//
// Transitions:
//   - pending -> success: 200ms cross-fade (visual continuity per §16.4.2).
//
// Visual DoD per §16.7:
//   - Success state renders both form names + signing date in MST + email-
//     confirmation copy + "Continue" CTA.
//   - Pending state shows spinner + auto-poll for 30s.
//   - Decline state offers "Try signing again" that fires fresh initiate.
//   - Timeout state surfaces "Refresh now" + support-email link.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getMyWaiverStatus,
  initiateWaiverSigning,
  hasFullySignedWaivers,
  PENDING_WAIVER_ENVELOPE_KEY,
} from "../services/waiverApi";
import "./WaiverReturn.css";

// Auto-poll cadence (Zuri §16.4.2): every 2s up to 30s.
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

const STATE = {
  LOADING: "loading", // initial GET /me before deciding pending vs success
  PENDING: "pending",
  SUCCESS: "success",
  DECLINED: "declined",
  TIMEOUT: "timeout",
  EXPIRED: "expired",
  ERROR: "error",
};

/**
 * Format an ISO timestamp as "Tue May 7, 2026 at 4:23pm MST" per Zuri §16.4.1.
 * Uses the user's locale for month/weekday but forces America/Phoenix tz +
 * "MST" suffix to match the studio's timezone-display convention.
 */
function formatSignedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Phoenix never observes DST so "MST" is always correct.
  const dateOpts = {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/Phoenix",
  };
  const timeOpts = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/Phoenix",
  };
  const datePart = d.toLocaleDateString("en-US", dateOpts);
  const timePart = d
    .toLocaleTimeString("en-US", timeOpts)
    .replace(" AM", "am")
    .replace(" PM", "pm");
  return `${datePart} at ${timePart} MST`;
}

const WaiverReturn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const urlStatus = searchParams.get("status");
  // urlEnvelope reserved for future correlation logging — read-only here.

  const [state, setState] = useState(() => {
    if (urlStatus === "declined") return STATE.DECLINED;
    if (urlStatus === "expired_token") return STATE.EXPIRED;
    return STATE.LOADING;
  });
  const [signedAt, setSignedAt] = useState(null);
  const [reInitiateError, setReInitiateError] = useState("");
  const [crossFading, setCrossFading] = useState(false);

  // Stable refs across the polling lifecycle so we never schedule duplicate
  // intervals across rerenders. The interval handle and the start timestamp
  // both live here.
  const pollHandleRef = useRef(null);
  const pollStartedAtRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollHandleRef.current) {
      clearInterval(pollHandleRef.current);
      pollHandleRef.current = null;
    }
  }, []);

  // Returnable cleanup: clear the localStorage correlation key once we land
  // on a terminal state so a stale envelope id never haunts the next visit.
  const clearCorrelation = useCallback(() => {
    try {
      localStorage.removeItem(PENDING_WAIVER_ENVELOPE_KEY);
      localStorage.removeItem("pendingWaiverDob");
      localStorage.removeItem("pendingWaiverGuardianName");
      // Note: pendingWaiverReturnTo is consumed by the success CTA, then
      // cleared at navigation time.
    } catch (storageErr) {
      // Non-fatal — private mode / quota.
    }
  }, []);

  /**
   * Single status-fetch + state-transition step. Used by both the initial
   * load and the recurring poll.
   */
  const fetchAndDispatch = useCallback(
    async ({ isInitial }) => {
      try {
        const response = await getMyWaiverStatus();
        const status = response.data || {};
        if (hasFullySignedWaivers(status)) {
          // Use the more recent of the two timestamps for the receipt label.
          const consentMs = status.consentSignedAt
            ? new Date(status.consentSignedAt).getTime()
            : 0;
          const releaseMs = status.releaseSignedAt
            ? new Date(status.releaseSignedAt).getTime()
            : 0;
          const latest =
            consentMs > releaseMs
              ? status.consentSignedAt
              : status.releaseSignedAt;
          setSignedAt(latest);

          // Cross-fade if we were polling; instant transition on initial load.
          if (!isInitial) {
            setCrossFading(true);
            setTimeout(() => {
              setState(STATE.SUCCESS);
              setCrossFading(false);
              clearCorrelation();
            }, 200);
          } else {
            setState(STATE.SUCCESS);
            clearCorrelation();
          }
          stopPolling();
          return true;
        }
        // Not yet signed -> stay in / move to pending.
        if (isInitial) {
          setState(STATE.PENDING);
          pollStartedAtRef.current = Date.now();
        }
        return false;
      } catch (err) {
        // 401 will be handled by the api refresh interceptor when consumers
        // use it; here we use a slim client without auto-refresh, so a hard
        // failure routes to the error state. The user can hit "Refresh".
        if (isInitial) {
          setState(STATE.ERROR);
          stopPolling();
        }
        return false;
      }
    },
    [stopPolling, clearCorrelation]
  );

  // Initial reconcile on mount (skip when the URL already pinned a terminal
  // state like declined/expired).
  useEffect(() => {
    if (state === STATE.DECLINED || state === STATE.EXPIRED) {
      return undefined;
    }
    fetchAndDispatch({ isInitial: true });
    // Intentionally empty dep array — initial mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-poll while in PENDING. Stops on terminal transition or unmount.
  useEffect(() => {
    if (state !== STATE.PENDING) return undefined;
    pollHandleRef.current = setInterval(() => {
      const startedAt = pollStartedAtRef.current || Date.now();
      const elapsed = Date.now() - startedAt;
      if (elapsed >= POLL_TIMEOUT_MS) {
        stopPolling();
        setState(STATE.TIMEOUT);
        return;
      }
      fetchAndDispatch({ isInitial: false });
    }, POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [state, fetchAndDispatch, stopPolling]);

  // Cleanup any timer on unmount.
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleContinueToPayment = () => {
    let returnTo = "/book-session";
    try {
      const stored = localStorage.getItem("pendingWaiverReturnTo");
      if (stored) returnTo = stored;
      localStorage.removeItem("pendingWaiverReturnTo");
    } catch (storageErr) {
      // ignore
    }
    navigate(returnTo);
  };

  const handleTrySigningAgain = async () => {
    setReInitiateError("");
    try {
      const response = await initiateWaiverSigning();
      const { signingUrl, envelopeId } = response.data || {};
      if (!signingUrl || !envelopeId) {
        throw new Error("waiver_response_missing_fields");
      }
      try {
        localStorage.setItem(PENDING_WAIVER_ENVELOPE_KEY, envelopeId);
      } catch (storageErr) {
        // ignore
      }
      window.location.href = signingUrl;
    } catch (err) {
      setReInitiateError(
        "Couldn't restart the signing flow. Please try again or email support@divitiadorastudio.com."
      );
    }
  };

  const handleManualRefresh = () => {
    setState(STATE.LOADING);
    fetchAndDispatch({ isInitial: true });
  };

  const handleReturnHome = () => navigate("/");
  const handleLogIn = () => navigate("/login");

  // ------- render branches -------

  if (state === STATE.LOADING) {
    return (
      <div className="waiver-return-page">
        <div className="waiver-return-card">
          <div className="waiver-return-spinner-row">
            <span
              className="waiver-return-spinner waiver-return-spinner-large"
              aria-hidden="true"
            />
            <p>Checking your signature status...</p>
          </div>
        </div>
      </div>
    );
  }

  if (state === STATE.SUCCESS) {
    return (
      <div className="waiver-return-page">
        <div
          className={`waiver-return-card waiver-return-card-success ${
            crossFading ? "waiver-return-fading-in" : ""
          }`}
        >
          <div className="waiver-return-icon waiver-return-icon-success">
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="24" cy="24" r="22" fill="#2F855A" />
              <path
                d="M14 24l7 7 13-14"
                stroke="#ffffff"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
          <h1 className="waiver-return-title">Thanks — your forms are signed.</h1>
          {signedAt && (
            <p className="waiver-return-meta">Signed on {formatSignedAt(signedAt)}</p>
          )}
          <ul className="waiver-return-forms-list">
            <li>Consent to Receive Infrared &amp; Red Light Therapy</li>
            <li>Client Acknowledgment &amp; Release of Liability</li>
          </ul>
          <p className="waiver-return-helper">
            We've also emailed copies to the address on your account.
          </p>
          <div className="waiver-return-cta-row">
            <button
              type="button"
              className="waiver-return-cta-primary"
              onClick={handleContinueToPayment}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state === STATE.PENDING) {
    return (
      <div className="waiver-return-page">
        <div className="waiver-return-card">
          <div className="waiver-return-spinner-row">
            <span
              className="waiver-return-spinner waiver-return-spinner-large"
              aria-hidden="true"
            />
          </div>
          <h1 className="waiver-return-title">Confirming your signature...</h1>
          <p className="waiver-return-helper">
            This usually takes a few seconds. Hang tight — we'll auto-update when
            your signature is confirmed.
          </p>
        </div>
      </div>
    );
  }

  if (state === STATE.DECLINED) {
    return (
      <div className="waiver-return-page">
        <div className="waiver-return-card">
          <div className="waiver-return-icon waiver-return-icon-info">i</div>
          <h1 className="waiver-return-title">
            Your signature wasn't completed
          </h1>
          <p className="waiver-return-helper">
            It looks like you cancelled or closed the signing page before
            finishing. No problem — you can sign now or come back later.
          </p>
          <p className="waiver-return-helper">
            Both forms are still required before your first booking.
          </p>
          {reInitiateError && (
            <div className="waiver-return-error" role="alert">
              {reInitiateError}
            </div>
          )}
          <div className="waiver-return-cta-row">
            <button
              type="button"
              className="waiver-return-cta-primary"
              onClick={handleTrySigningAgain}
            >
              Try signing again
            </button>
            <button
              type="button"
              className="waiver-return-cta-secondary"
              onClick={handleReturnHome}
            >
              Cancel and return to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state === STATE.TIMEOUT) {
    return (
      <div className="waiver-return-page">
        <div className="waiver-return-card">
          <div className="waiver-return-icon waiver-return-icon-warning">!</div>
          <h1 className="waiver-return-title">
            Still confirming your signature...
          </h1>
          <p className="waiver-return-helper">
            This is taking longer than usual. Your signature was recorded — we
            just haven't finished syncing yet.
          </p>
          <ul className="waiver-return-helper-list">
            <li>Refresh this page in a minute</li>
            <li>
              Email{" "}
              <a href="mailto:support@divitiadorastudio.com">
                support@divitiadorastudio.com
              </a>{" "}
              if it persists
            </li>
          </ul>
          <div className="waiver-return-cta-row">
            <button
              type="button"
              className="waiver-return-cta-primary"
              onClick={handleManualRefresh}
            >
              Refresh now
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state === STATE.EXPIRED) {
    return (
      <div className="waiver-return-page">
        <div className="waiver-return-card">
          <div className="waiver-return-icon waiver-return-icon-info">i</div>
          <h1 className="waiver-return-title">This signing link has expired</h1>
          <p className="waiver-return-helper">
            The link in your email is more than 30 days old. No problem — you
            can sign by logging in to your account.
          </p>
          <div className="waiver-return-cta-row">
            <button
              type="button"
              className="waiver-return-cta-primary"
              onClick={handleLogIn}
            >
              Log in and sign now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // STATE.ERROR
  return (
    <div className="waiver-return-page">
      <div className="waiver-return-card">
        <div className="waiver-return-icon waiver-return-icon-error">!</div>
        <h1 className="waiver-return-title">Something went wrong</h1>
        <p className="waiver-return-helper">
          We couldn't check your signature status. Try refreshing the page, or
          email{" "}
          <a href="mailto:support@divitiadorastudio.com">
            support@divitiadorastudio.com
          </a>{" "}
          if it keeps happening.
        </p>
        <div className="waiver-return-cta-row">
          <button
            type="button"
            className="waiver-return-cta-primary"
            onClick={handleManualRefresh}
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
};

export default WaiverReturn;
